import { useContext, useEffect, useState, useRef } from 'react';
import { TestingHqContext } from '../../context/TestingHqContext';
import { TestingContext } from '../../context/TestingContext';

import List from '../../components/list/List';
import { get_testing_accept_list, get_testing_type, get_testing_list, get_testing_place_list } from '../../utils/restapi';
import TESTING_ACCEPT_STATUS from '../../constants/testing_accept';

import './list.css';

const TestingAcceptList = () => {
  const { testingHqs } = useContext(TestingHqContext);
  const { testingObj, setTestingObj } = useContext(TestingContext);

  const listRef = useRef(null);

  // 선택상자 목록들
  const [testingTypes, setTestingTypes] = useState([]);
  const [testings, setTestings] = useState([]);
  const [testingPlaces, setTestingPlaces] = useState([]);

  const [testingHqObj, setTestingHqObj] = useState(null);
  const [testingPlaceObj, setTestingPlaceObj] = useState(null);

  const [selectedStatus, setSelectedStatus] = useState('');

  // 1. 초기 데이터 로딩만
  useEffect(() => {
    getTestingTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 2. testingHqs 변경 시 고사장 목록 업데이트
  useEffect(() => {
    if (testingHqs.length) {
      getTestingPlaces();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testingHqs]);

  // 3. testingObj.type 변경 시 관련 데이터 초기화
  useEffect(() => {
    if (testingObj?.type) {
      getTesting(testingObj.type);
      getTestingPlaces();
    } else {
      setTestings([]);
      setTestingPlaces([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testingObj?.type]);

  // 4. testingObj.testing_id 변경 시 고사장 목록 업데이트
  useEffect(() => {
    if (testingObj?.testing_id) {
      getTestingPlaces();
    } else {
      setTestingPlaces([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [testingObj?.testing_id]);

  // ★ 5. 핵심: List 새로고침이 필요한 조건들 (목록 갱신)
  useEffect(() => {
    // 기본 조건이 충족된 경우에만 목록 새로고침
    if (testingObj?.type && testingObj?.testing_id && listRef.current) {
      console.log("목록 조건 변경 - List 새로고침:", {
        type: testingObj.type,
        testing_id: testingObj.testing_id,
        testing_place_id: testingPlaceObj?.testing_place_id,
        hq_code: testingHqObj?.code,
        status: selectedStatus
      });
      
      // List 컴포넌트 새로고침 (handleGetData는 List 내부에서 자동 호출)
      setTimeout(() => {
        if (listRef.current) {
          listRef.current.refresh();
        }
      }, 100);
    } else if (listRef.current) {
      // 조건이 미충족이면 목록 비우기
      listRef.current.resetItems();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    testingObj?.type,           // 시험 유형 변경
    testingObj?.testing_id,     // 시험 변경
    testingHqObj?.code,         // 지역본부 변경
    testingPlaceObj?.testing_place_id,  // 고사장 변경
    selectedStatus              // 상태 변경
  ]);

  // List에서 사용할 데이터 가져오기 함수
  const handleGetData = async (params) => {
    // 필수 조건 체크
    if (!testingObj?.type || !testingObj?.testing_id) {
      console.log("[handleGetData] 필수 조건 미충족");
      return { lists: [], total: 0, totalPages: 0, page: 1, rowsPerPage: 20 };
    }

    const requestParams = {
      type: parseInt(testingObj.type),
      testing_id: parseInt(testingObj.testing_id),
      page: params?.page || 1,
      rowsPerPage: params?.rowsPerPage || 20,
      sort: params?.sort || 'a_testing_accept_id',
      desc: params?.desc || 'desc',
      statusArray: TESTING_ACCEPT_STATUS.used || [],
      ...params
    };

    //상태가 선택된 경우에만 추가
    if (selectedStatus !== '') {
      requestParams.status = selectedStatus;
    }

    // 고사장이 선택된 경우에만 추가
    if (testingPlaceObj?.testing_place_id) {
      requestParams.testing_place_id = testingPlaceObj.testing_place_id;
    }

    // 지역본부가 선택된 경우
    if (testingHqObj?.code) {
      requestParams.hcodes = [testingHqObj.code];
    } else if (testingHqs?.length > 0) {
      requestParams.hcodes = testingHqs.map(hq => hq.code);
    }

    console.log("[handleGetData] requestParams:", requestParams);
    
    try {
      const response = await get_testing_accept_list(requestParams);
      console.log("[handleGetData] response:", response);
      return {
        ...response,
        page: response.page || requestParams.page,
        rowsPerPage: response.rowsPerPage || requestParams.rowsPerPage,
      };
    } catch (error) {
      console.error("[handleGetData] error:", error);
      return { lists: [], total: 0, totalPages: 0, page: 1, rowsPerPage: 20 };
    }
  };

  // 시험유형 데이터 가져오기
  const getTestingTypes = async () => {
    console.log('getTestingTypes called');
    try {
      const response = await get_testing_type();
      console.log("getTestingTypes:", response);
      setTestingTypes(response);
    } catch (error) {
      console.error("Error fetching types:", error);
    }
  };

  // 시험에 대한 데이터 가져오기
  const getTesting = async (type) => {
    console.log(`getTesting called : ${type}`);
    try {
      const response = await get_testing_list({ type, page:1, rowsPerPage:1000, sort:'a_testing_id', desc:'desc' });
      console.log("getTesting:", response);
      setTestings(response.lists || response);
    } catch (error) {
      console.error("Error fetching testing:", error);
      setTestings([]);
    }
  };

  // 고사장에 대한 데이터 가져오기
  const getTestingPlaces = async () => {
    console.log(`getTestingPlaces called`);

    if (testingHqs.length === 0) {
      setTestingPlaces([]);
      setTestingPlaceObj(null);
      return;
    }

    const json = {
      testing_id: testingObj?.testing_id || 0, // 수정: ?. 사용
      gcodes: testingHqs.map(hq => hq.code).join(','),
      page: 1,
      rowsPerPage: 1000,
      sort: 'a_gcode,a_code',
    };

    console.log("getTestingPlace params:", json);

    try {
      const response = await get_testing_place_list(json);
      console.log("getTestingPlaces:", response);
      setTestingPlaces(response.lists);
      if(response.lists.length === 1) {
        setTestingPlaceObj(response.lists[0]);
      } else {
        setTestingPlaceObj(null);
      }
    } catch (error) {
      console.error("Error fetching place:", error);
      setTestingPlaces([]);
    }
  };

  // 시험유형 선택 핸들러
  const handleTestingTypeChange = (e) => {
    console.log('handleTestingTypeChange called');
    const selectedValue = e.target.value;
    setTestingObj({ ...testingObj, type: selectedValue });
    
    // 하위 선택값들 초기화
    setTestingPlaceObj(null);
    setTestingHqObj(null);
  };

  // 시험 선택 핸들러 수정
  const handleTestingIdChange = (e) => {
    console.log('handleTestingIdChange called');
    const selectedValue = e.target.value;
    const selectedTesting = testings.find(obj => obj.testing_id === parseInt(selectedValue));
    setTestingObj(selectedTesting || null);
    
    // 하위 선택값들 초기화
    setTestingPlaceObj(null);
  };

  // 지역본부 선택 핸들러 수정
  const handleTestingHqChange = (e) => {
    console.log('handleTestingHqChange called');
    const selectedValue = e.target.value;
    const testingHqObj = testingHqs.find(hq => hq.code === selectedValue);
    setTestingHqObj(testingHqObj || null);
    
    // 고사장 선택 초기화
    setTestingPlaceObj(null);
  };

  // 고사장 선택 핸들러 수정
  const handleTestingPlaceChange = (e) => {
    console.log('handleTestingPlaceChange called');
    const selectedValue = e.target.value;
    const selectedTestingPlace = testingPlaces.find(obj => obj.testing_place_id === parseInt(selectedValue));
    setTestingPlaceObj(selectedTestingPlace || null);
  };

  // 상태 변경 핸들러 추가
  const handleSelectedStatusChange = (e) => {
    console.log('handleSelectedStatusChange called');
    const selectedValue = parseInt(e.target.value) || 0;
    setSelectedStatus(selectedValue);
  };

  // headers에 renderCell 함수를 포함시킴
  const headers = [
    { 
      name: 'NO.', 
      value: 'a_testing_accept_id',
      renderCell: (item) => item.testing_accept_id
    },
    { 
      name: '접수번호', 
      value: 'a_examno',
      renderCell: (item) => item.examno
    },
    { 
      name: '수험번호', 
      value: 'a_code',
      renderCell: (item) => item.code
    },
    { 
      name: '성명', 
      value: 'a_name',
      renderCell: (item) => item.personObj.name
    },
    { 
      name: '생년월일', 
      value: 'a_maxCount',
      renderCell: (item) => item.personObj.birth
    },
    { 
      name: '고사장', 
      value: 'c_name',
      renderCell: (item) => item.testingPlaceObj.name
    },
    { 
      name: '고사실', 
      value: 'a_room',
      renderCell: (item) => item.room_no
    },
    { 
      name: '좌석번호', 
      value: '',
      renderCell: (item) => item.seat_no
    },
    { 
      name: '상태', 
      value: '',
      renderCell: (item) => TESTING_ACCEPT_STATUS.name[item.status] || item.status
    },
  ];

  // 행 클릭 이벤트 (옵션)
  const handleRowClick = (item) => {
    console.log('Row clicked:', item);
    // 필요시 행 클릭 시 동작 정의
  };

  return (
    <div id="testing_accept_list">
      <div className="head">
        <h1>회차별 고사장 목록</h1>
      </div>
      <div className="body">
        <div className="top">
          <ul className="form">
            <li>
              <label>구분</label>
              <span>
                <select value={testingObj?.type || ''} onChange={handleTestingTypeChange}>
                  <option value="">▒ 선택 ▒</option>
                  {testingTypes.map((obj, index) => (
                    <option key={index} value={obj.code}>
                      {obj.nickname || obj.name}
                    </option>
                  ))}
                </select>
              </span>
            </li>
            <li>
              <label>시험</label>
              <span>
                <select value={testingObj?.testing_id || ''} onChange={handleTestingIdChange}>
                  <option value="">▒ 선택 ▒</option>
                  {testings.map((obj, index) => (
                    <option key={index} value={obj.testing_id}>
                      {obj.title}
                    </option>
                  ))}
                </select>
              </span>
            </li>
            <li>
              <label>지역본부</label>
              <span>
                <select value={testingHqObj?.code || ''} onChange={handleTestingHqChange}>
                  <option value="">▒ 선택 ▒</option>
                  {testingHqs.map((hq, index) => (
                    <option key={index} value={hq.code}>
                      {hq.name}
                    </option>
                  ))}
                </select>
              </span>
            </li>
            <li>
              <label>고사장</label>
              <span>
                <select value={testingPlaceObj?.testing_place_id || 0} onChange={handleTestingPlaceChange}>
                  <option value="">▒ 고사장 ▒</option>
                  {testingPlaces.map((obj, index) => (
                    <option key={index} value={obj.testing_place_id}>
                      {obj.name}
                    </option>
                  ))}
                </select>
              </span>
            </li>
            <li>
              <label>상태</label>
              <span>
                <select value={selectedStatus || 0} onChange={handleSelectedStatusChange}>
                  <option value={0}>▒ 전체 ▒</option>
                  {Object.entries(TESTING_ACCEPT_STATUS.used).map(([key, value]) => (
                    <option key={TESTING_ACCEPT_STATUS.value[key]} value={TESTING_ACCEPT_STATUS.value[key]}>
                      {TESTING_ACCEPT_STATUS.name[value]}
                    </option>
                  ))}
                </select>
              </span>
            </li>
          </ul>
        </div>
        <div className="middle">
          <List
            ref={listRef}
            key={`${testingObj?.testing_id}-${testingHqObj?.code}-${testingPlaceObj?.testing_place_id}-${selectedStatus}`}
            headers={headers}
            onGetData={handleGetData}
            onRowClick={handleRowClick}
            className="testing_accept_table"
            initialSort="a_testing_accept_id"
            initialDesc="desc"
            initialRowsPerPage={20}
          />
        </div>
        <div className="bottom">
        </div>
      </div>
    </div>
  );
};

export default TestingAcceptList;
